import { ReactNode, useState, useTransition } from 'react';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import { Box, Bones } from '@simplywallst/ui-core';
import useResizeObserver from 'use-resize-observer';
import { ViewMode } from '@/pages/CompanyReport';
import { useSelector } from 'react-redux';
import { getViewMode } from '@/redux/location/selectors';

type Props = {
  children: ReactNode;
  initialMinHeight?: number;
};

/**
 * On client, it will only render the component if it's in the viewport.
 * As soon as it's rendered for the first time, it will measure the height
 * so that upon revisiting the component there is no CLS.
 *
 * By default it renders on the server to allow for SEO indexing.
 */
const IntersectionRenderer = ({ children, initialMinHeight = 300 }: Props) => {
  const viewMode = useSelector(getViewMode);
  const isPrintMode = viewMode === ViewMode.Print;
  const [isPending, startTransition] = useTransition();
  const [inView, setInView] = useState(false);
  const [lastHeight, setLastHeight] = useState<number | null>(null);
  const observerDisabled = isPrintMode || RUNTIME_ENV === 'server';
  const targetInView = observerDisabled || inView;

  const { ref } = useResizeObserver({
    onResize: ({ height }) => {
      targetInView &&
        !isPending &&
        height &&
        height !== lastHeight &&
        setLastHeight(height);
    },
  });

  const minHeight =
    lastHeight && lastHeight > 0 ? lastHeight : initialMinHeight;

  return (
    <IntersectionObserver
      threshold={0}
      onChange={({ isIntersecting }) => {
        startTransition(() => {
          setInView(isIntersecting);
        });
      }}
      disabled={observerDisabled}
    >
      <Box ref={ref} minHeight={`${minHeight}px`}>
        {targetInView && !isPending ? (
          children
        ) : (
          <Bones minHeight={`${minHeight}px`} />
        )}
      </Box>
    </IntersectionObserver>
  );
};

export default IntersectionRenderer;
